/**
 * Handbuch — In-App-Dokumentation mit Markdown-Rendering.
 *
 * Features:
 * - Seitennavigation mit Kapitel-Liste
 * - Volltextsuche über alle Handbuch-Seiten
 * - Markdown-Rendering mit farbigen Blockquotes (Tipp, Warnung, Schritt)
 * - Dynamisches Routing per Slug
 *
 * @page /handbuch, /handbuch/:slug
 * @roles alle (authentifiziert)
 * @i18n handbook, common
 * @endpoints GET /api/handbuch, GET /api/handbuch/:slug, GET /api/handbuch/search
 */
import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { BookOpen, Download, RefreshCw } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';

const API = import.meta.env.VITE_API_URL || '';

// Custom Markdown Renderers
const mdComponents = {
  // Blockquotes als farbige Info-Boxen
  blockquote: ({ children }) => {
    // Extrahiere Text-Content aus children
    const extractText = (node) => {
      if (typeof node === 'string') return node;
      if (!node) return '';
      if (node.props?.children) {
        const c = node.props.children;
        if (Array.isArray(c)) return c.map(extractText).join('');
        return extractText(c);
      }
      return '';
    };
    const text = Array.isArray(children) ? children.map(extractText).join('') : extractText(children);

    // Tipp (grün)
    if (text.includes('\u{1F4A1}') || text.includes('Tipp')) {
      return (
        <div className="bg-emerald-50 dark:bg-emerald-900/20 border-l-4 border-emerald-500 rounded-r-lg p-4 my-4 not-prose">
          <div className="text-sm text-emerald-900 dark:text-emerald-200 [&>p]:my-1 [&>p:first-child]:mt-0 [&>p:last-child]:mb-0 [&_strong]:text-emerald-800 dark:[&_strong]:text-emerald-100">{children}</div>
        </div>
      );
    }

    // Warnung (amber)
    if (text.includes('⚠') || text.includes('Wichtig') || text.includes('Achtung')) {
      return (
        <div className="bg-amber-50 dark:bg-amber-900/20 border-l-4 border-amber-500 rounded-r-lg p-4 my-4 not-prose">
          <div className="text-sm text-amber-900 dark:text-amber-200 [&>p]:my-1 [&>p:first-child]:mt-0 [&>p:last-child]:mb-0 [&_strong]:text-amber-800 dark:[&_strong]:text-amber-100">{children}</div>
        </div>
      );
    }

    // Schritt (blau)
    if (text.includes('\u{1F4CC}') || text.includes('Schritt')) {
      return (
        <div className="bg-primary-50 dark:bg-primary-900/20 border-l-4 border-[#338edc] rounded-r-lg p-4 my-4 not-prose">
          <div className="text-sm text-primary-900 dark:text-primary-200 [&>p]:my-1 [&>p:first-child]:mt-0 [&>p:last-child]:mb-0 [&_strong]:text-primary-800 dark:[&_strong]:text-primary-100">{children}</div>
        </div>
      );
    }

    // Default Blockquote (grau)
    return (
      <div className="bg-gray-50 dark:bg-gray-800/50 border-l-4 border-[var(--border-default,#E5E7EB)] dark:border-gray-600 rounded-r-lg p-4 my-4 not-prose">
        <div className="text-sm text-[var(--text-primary,#1A1A2E)] dark:text-[var(--text-tertiary,#6B7280)] italic [&>p]:my-1 [&>p:first-child]:mt-0 [&>p:last-child]:mb-0">{children}</div>
      </div>
    );
  },

  // Überschriften
  h2: ({ children }) => (
    <h2 className="text-xl font-bold mt-8 mb-3 pb-2 border-b border-[var(--border-default,#E5E7EB)] dark:border-gray-700 text-[var(--text-primary,#1A1A2E)]">
      {children}
    </h2>
  ),
  h3: ({ children }) => (
    <h3 className="text-base font-medium mt-6 mb-2 text-[var(--text-primary,#1A1A2E)]">
      {children}
    </h3>
  ),

  // Listen
  ul: ({ children }) => (
    <ul className="list-disc list-outside pl-5 space-y-1.5 my-3 text-[var(--text-primary,#1A1A2E)] dark:text-[var(--text-tertiary,#6B7280)]">
      {children}
    </ul>
  ),
  ol: ({ children }) => (
    <ol className="list-decimal list-outside pl-5 space-y-2 my-3 text-[var(--text-primary,#1A1A2E)] dark:text-[var(--text-tertiary,#6B7280)]">
      {children}
    </ol>
  ),
  li: ({ children }) => (
    <li className="text-[var(--text-primary,#1A1A2E)] dark:text-[var(--text-tertiary,#6B7280)] leading-relaxed">{children}</li>
  ),

  // Code
  code: ({ inline, className, children }) => {
    if (inline !== false && !className) {
      return (
        <code className="bg-gray-100 dark:bg-gray-800 text-emerald-600 dark:text-emerald-400 px-1.5 py-0.5 rounded text-sm font-mono">
          {children}
        </code>
      );
    }
    return (
      <pre className="bg-gray-900 text-gray-100 rounded-lg p-4 overflow-x-auto my-4">
        <code className="text-sm font-mono">{children}</code>
      </pre>
    );
  },

  // Tabellen
  table: ({ children }) => (
    <div className="overflow-x-auto my-4 rounded-card border border-[var(--border-default,#E5E7EB)] dark:border-gray-700">
      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
        {children}
      </table>
    </div>
  ),
  thead: ({ children }) => (
    <thead className="bg-[var(--bg-inset,#F8F9FC)]">{children}</thead>
  ),
  th: ({ children }) => (
    <th className="px-4 py-2.5 text-left text-xs font-semibold text-[var(--text-secondary,#4B5563)] dark:text-[var(--text-tertiary,#6B7280)] uppercase tracking-wider">
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td className="px-4 py-2.5 text-sm text-[var(--text-primary,#1A1A2E)] dark:text-[var(--text-tertiary,#6B7280)] border-t border-[var(--border-table,#F3F4F6)] dark:border-gray-800">
      {children}
    </td>
  ),

  // HR
  hr: () => <hr className="my-8 border-[var(--border-default,#E5E7EB)] dark:border-gray-700" />,

  // Absätze
  p: ({ children }) => (
    <p className="my-3 text-[var(--text-primary,#1A1A2E)] dark:text-[var(--text-tertiary,#6B7280)] leading-relaxed">{children}</p>
  ),

  // Fett
  strong: ({ children }) => (
    <strong className="font-semibold text-[var(--text-primary,#1A1A2E)]">{children}</strong>
  ),

  // Links
  a: ({ href, children }) => (
    <a href={href} className="text-emerald-600 dark:text-emerald-400 hover:underline" target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  ),
};

export default function Handbuch() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const isAdmin = user?.rolle === 'administrator';
  const { slug } = useParams();
  const navigate = useNavigate();
  const [kapitel, setKapitel] = useState([]);
  const [artikel, setArtikel] = useState(null);
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [hrPdfLoading, setHrPdfLoading] = useState(false);
  const [hrPdfRegenerating, setHrPdfRegenerating] = useState(false);

  const downloadPdf = async () => {
    setPdfLoading(true);
    try {
      const res = await fetch(`${API}/api/handbuch/pdf`, { credentials: 'include' });
      if (!res.ok) throw new Error('PDF-Download fehlgeschlagen');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `WeShip-v2-Handbuch.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('PDF download error:', err);
    } finally {
      setPdfLoading(false);
    }
  };

  const downloadHrPdf = async () => {
    setHrPdfLoading(true);
    try {
      const res = await fetch(`${API}/api/handbuch/hr-pdf`, { credentials: 'include' });
      if (!res.ok) throw new Error('HR-PDF-Download fehlgeschlagen');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `WeShip-HR-Handbuch.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('HR-PDF download error:', err);
    } finally {
      setHrPdfLoading(false);
    }
  };

  const regenerateHrPdf = async () => {
    setHrPdfRegenerating(true);
    try {
      const res = await fetch(`${API}/api/handbuch/hr-pdf/regenerate`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Regenerierung fehlgeschlagen');
      alert(t('manual.hrPdfRegenerated', 'HR-Handbuch wurde neu erstellt') + ` (v${data.version}, ${data.sizeKb} KB)`);
    } catch (err) {
      console.error('HR-PDF regenerate error:', err);
      alert(`Fehler: ${err.message}`);
    } finally {
      setHrPdfRegenerating(false);
    }
  };

  // Lade Kapitel-Übersicht
  useEffect(() => {
    fetch(`${API}/api/handbuch`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => {
        setKapitel(d.kapitel || []);
        if (!slug && d.kapitel?.length && d.kapitel[0].artikel?.length) {
          navigate(`/handbuch/${d.kapitel[0].artikel[0].slug}`, { replace: true });
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Lade Artikel bei Slug-Änderung
  useEffect(() => {
    if (!slug) return;
    setArtikel(null);
    fetch(`${API}/api/handbuch/${slug}`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => { if (d.id) setArtikel(d); })
      .catch(() => {});
  }, [slug]);

  // Debounced search
  const doSearch = useCallback((q) => {
    if (!q.trim()) { setSearchResults(null); return; }
    fetch(`${API}/api/handbuch/search?q=${encodeURIComponent(q)}`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => setSearchResults(d.results || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => doSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search, doSearch]);

  const formatDate = (d) => {
    if (!d) return '';
    return new Date(d).toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin h-6 w-6 border-2 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="flex h-full">
      {/* Sidebar */}
      <aside className={`${sidebarOpen ? 'w-72' : 'w-0 overflow-hidden'} border-r border-line bg-surface flex-shrink-0 flex flex-col transition-all duration-200`}>
        <div className="p-4 border-b border-line">
          <div className="relative mb-2">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-content-tertiary" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" />
            </svg>
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={t('manual.searchPlaceholder')}
              className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-line bg-surface-elevated text-content placeholder-content-tertiary focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500"
            />
          </div>
          <button
            onClick={downloadPdf}
            disabled={pdfLoading}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm rounded-lg border border-line bg-surface-elevated text-content-secondary hover:bg-surface-hover transition-colors disabled:opacity-50"
          >
            {pdfLoading ? (
              <div className="animate-spin h-4 w-4 border-2 border-[#338edc] border-t-transparent rounded-full" />
            ) : (
              <Download className="w-4 h-4" />
            )}
            {pdfLoading ? t('manual.generatingPdf', 'PDF wird erstellt...') : t('manual.downloadPdf', 'Als PDF herunterladen')}
          </button>
          <div className="mt-2 flex gap-2">
            <button
              onClick={downloadHrPdf}
              disabled={hrPdfLoading}
              className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm rounded-lg border border-line bg-surface-elevated text-content-secondary hover:bg-surface-hover transition-colors disabled:opacity-50"
              title={t('manual.hrPdfHint', 'Vollständiges HR-Handbuch (Stammdaten, Verträge, Zeitkonto, …)')}
            >
              {hrPdfLoading ? (
                <div className="animate-spin h-4 w-4 border-2 border-[#338edc] border-t-transparent rounded-full" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              {hrPdfLoading ? t('manual.generatingPdf', 'PDF wird erstellt...') : t('manual.downloadHrPdf', 'HR-Handbuch')}
            </button>
            {isAdmin && (
              <button
                onClick={regenerateHrPdf}
                disabled={hrPdfRegenerating}
                className="px-3 py-2 text-sm rounded-lg border border-line bg-surface-elevated text-content-secondary hover:bg-surface-hover transition-colors disabled:opacity-50"
                title={t('manual.regenerateHrPdf', 'HR-Handbuch neu generieren (Admin)')}
              >
                <RefreshCw className={`w-4 h-4 ${hrPdfRegenerating ? 'animate-spin' : ''}`} />
              </button>
            )}
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto p-3">
          {searchResults !== null ? (
            <div>
              <div className="text-xs text-content-secondary font-medium px-2 mb-2">
                {searchResults.length} {t('manual.results', { n: searchResults.length })}
              </div>
              {searchResults.map(r => (
                <button
                  key={r.slug}
                  onClick={() => { navigate(`/handbuch/${r.slug}`); setSearch(''); setSearchResults(null); }}
                  className={`block w-full text-left px-3 py-2 rounded-lg text-sm mb-0.5 transition-colors ${
                    slug === r.slug ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400' : 'hover:bg-surface-hover text-content-secondary'
                  }`}
                >
                  <span>{r.icon} {r.titel}</span>
                  <span className="block text-xs text-content-tertiary mt-0.5">{r.kapitel}</span>
                </button>
              ))}
            </div>
          ) : (
            kapitel.map(k => (
              <div key={k.name} className="mb-3">
                <div className="text-[10px] font-semibold text-content-tertiary uppercase tracking-widest px-2 mb-1.5 mt-3 first:mt-0">{k.name}</div>
                {k.artikel.map(a => (
                  <button
                    key={a.slug}
                    onClick={() => navigate(`/handbuch/${a.slug}`)}
                    className={`block w-full text-left px-3 py-1.5 rounded-lg text-sm mb-0.5 transition-colors ${
                      slug === a.slug ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 font-medium' : 'hover:bg-surface-hover text-content-secondary'
                    }`}
                  >
                    {a.icon} {a.titel}
                  </button>
                ))}
              </div>
            ))
          )}
        </nav>
      </aside>

      {/* Toggle Sidebar */}
      <button
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className="flex-shrink-0 w-6 flex items-center justify-center hover:bg-surface-hover text-content-tertiary transition-colors"
        title={sidebarOpen ? t('manual.collapseNav') : t('manual.expandNav')}
      >
        <svg className={`w-4 h-4 transition-transform ${sidebarOpen ? '' : 'rotate-180'}`} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
          <path d="m15 19-7-7 7-7" />
        </svg>
      </button>

      {/* Content */}
      <main className="flex-1 overflow-y-auto">
        {artikel ? (
          <div className="p-8">
            <div className="mb-6">
              <div className="flex items-center gap-2 text-sm text-content-secondary mb-2">
                <span>{artikel.kapitel}</span>
                {artikel.version && (
                  <>
                    <span>·</span>
                    <span className="bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 px-2 py-0.5 rounded text-xs font-mono">v{artikel.version}</span>
                  </>
                )}
              </div>
              <h1 className="text-xl font-semibold text-content">{artikel.icon} {artikel.titel}</h1>
              <p className="text-sm text-content-secondary mt-1">{t('manual.lastUpdated')}: {formatDate(artikel.updated_at)}</p>
              {artikel.tags?.length > 0 && (
                <div className="flex gap-1.5 mt-2 flex-wrap">
                  {artikel.tags.map(tag => (
                    <span key={tag} className="px-2 py-0.5 bg-surface-elevated text-content-secondary text-xs rounded">{tag}</span>
                  ))}
                </div>
              )}
            </div>
            <div className="prose prose-lg dark:prose-invert max-w-none">
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>{artikel.inhalt}</ReactMarkdown>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center h-64 text-content-secondary">
            <div className="text-center">
              <BookOpen className="w-10 h-10 text-content-tertiary mx-auto mb-3" />
              <p>{t('manual.selectArticle')}</p>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
